/**
 * Network Reconnection Manager
 *
 * Coordinates automatic reconnection attempts when network connectivity is lost.
 * Features:
 * - Listens to browser online/offline events
 * - Performs lightweight health checks to backend
 * - Implements exponential backoff retry strategy
 * - Event-based architecture for component integration
 */

type EventType =
  | 'connectionLost'
  | 'connectionRestored'
  | 'scheduleRetry'
  | 'checkingConnection';

type EventCallback = (data?: any) => void;

interface ScheduleRetryData {
  attemptNumber: number;
  delayMs: number;
  nextRetryIn: number; // seconds
}

interface CheckingConnectionData {
  attemptNumber: number;
}

/**
 * Exponential backoff intervals (in milliseconds)
 * [1s, 2s, 4s, 8s, 15s, 30s, 30s, ...]
 */
const RETRY_INTERVALS = [1000, 2000, 4000, 8000, 15000, 30000];

/**
 * Maximum retry interval (30 seconds)
 */
const MAX_RETRY_INTERVAL = 30000;

/**
 * Network Reconnection Manager (Singleton)
 *
 * Manages automatic reconnection attempts when network connectivity is lost.
 * Uses browser online/offline events combined with backend health checks.
 */
class NetworkReconnectionManager {
  private static instance: NetworkReconnectionManager | null = null;

  private eventListeners: Map<EventType, Set<EventCallback>> = new Map();
  private isReconnecting = false;
  private reconnectionAttempt = 0;
  private reconnectionTimer: NodeJS.Timeout | null = null;
  private onlineHandler: (() => void) | null = null;
  private offlineHandler: (() => void) | null = null;

  private constructor() {
    this.initializeBrowserEventListeners();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): NetworkReconnectionManager {
    if (!NetworkReconnectionManager.instance) {
      NetworkReconnectionManager.instance = new NetworkReconnectionManager();
    }
    return NetworkReconnectionManager.instance;
  }

  /**
   * Initialize browser online/offline event listeners
   */
  private initializeBrowserEventListeners() {
    this.onlineHandler = () => {
      console.log('[NetworkReconnectionManager] Browser reports online');
      // Browser thinks we're online, but verify with backend
      this.performConnectivityCheck();
    };

    this.offlineHandler = async () => {
      console.log('[NetworkReconnectionManager] Browser reports offline - verifying backend');
      // Don't blindly trust the browser offline event: the backend may be running
      // locally (on-premise) and still reachable even without internet access.
      try {
        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), 3000);
        const response = await fetch('/api/v1/health', {
          method: 'HEAD',
          signal: controller.signal,
          cache: 'no-cache'
        });
        clearTimeout(t);
        if (response.ok) {
          console.log('[NetworkReconnectionManager] Backend still reachable, ignoring offline event');
          return; // local backend is up — no need to show error
        }
      } catch {
        // Backend unreachable — fall through and declare connection lost
      }
      this.emit('connectionLost');
    };

    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);
  }

  /**
   * Start reconnection attempts
   * Called when a network error occurs
   */
  public startReconnectionAttempts() {
    if (this.isReconnecting) {
      console.log('[NetworkReconnectionManager] Already reconnecting, skipping');
      return;
    }

    console.log('[NetworkReconnectionManager] Starting reconnection attempts');
    this.isReconnecting = true;
    this.reconnectionAttempt = 0;
    this.scheduleNextCheck();
  }

  /**
   * Stop reconnection attempts
   * Called when connection is restored or error overlay is hidden
   */
  public stopReconnectionAttempts() {
    console.log('[NetworkReconnectionManager] Stopping reconnection attempts');
    this.isReconnecting = false;
    this.reconnectionAttempt = 0;

    if (this.reconnectionTimer) {
      clearTimeout(this.reconnectionTimer);
      this.reconnectionTimer = null;
    }
  }

  /**
   * Schedule the next connectivity check with exponential backoff
   */
  private scheduleNextCheck() {
    if (!this.isReconnecting) {
      return;
    }

    this.reconnectionAttempt++;

    // Get delay for this attempt (with exponential backoff)
    const delayMs = this.getRetryDelay(this.reconnectionAttempt);
    const delaySeconds = Math.floor(delayMs / 1000);

    console.log(
      `[NetworkReconnectionManager] Scheduling check in ${delayMs}ms (attempt ${this.reconnectionAttempt})`
    );

    // Notify listeners about scheduled retry
    this.emit('scheduleRetry', {
      attemptNumber: this.reconnectionAttempt,
      delayMs,
      nextRetryIn: delaySeconds
    } as ScheduleRetryData);

    // Schedule the check
    this.reconnectionTimer = setTimeout(() => {
      this.performConnectivityCheck();
    }, delayMs);
  }

  /**
   * Get retry delay using exponential backoff
   */
  private getRetryDelay(attemptNumber: number): number {
    const index = Math.min(attemptNumber - 1, RETRY_INTERVALS.length - 1);
    return RETRY_INTERVALS[index] || MAX_RETRY_INTERVAL;
  }

  /**
   * Perform connectivity check to backend
   * Uses lightweight HEAD request to health endpoint
   */
  public async performConnectivityCheck() {
    if (!this.isReconnecting) {
      return;
    }

    console.log(
      `[NetworkReconnectionManager] Checking connectivity (attempt ${this.reconnectionAttempt})`
    );

    // Notify listeners that we're checking
    this.emit('checkingConnection', {
      attemptNumber: this.reconnectionAttempt
    } as CheckingConnectionData);

    try {
      // Lightweight HEAD request to health endpoint
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch('/api/v1/health', {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-cache'
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        console.log('[NetworkReconnectionManager] Connection restored');
        this.handleConnectionRestored();
      } else {
        console.log(
          `[NetworkReconnectionManager] Check failed with status ${response.status}, retrying...`
        );
        this.scheduleNextCheck();
      }
    } catch (error) {
      console.log('[NetworkReconnectionManager] Check failed, retrying...', error);
      this.scheduleNextCheck();
    }
  }

  /**
   * Handle successful connection restoration
   */
  private handleConnectionRestored() {
    this.isReconnecting = false;
    this.reconnectionAttempt = 0;

    if (this.reconnectionTimer) {
      clearTimeout(this.reconnectionTimer);
      this.reconnectionTimer = null;
    }

    // Notify all listeners that connection is restored
    this.emit('connectionRestored');
  }

  /**
   * Subscribe to events
   */
  public on(event: EventType, callback: EventCallback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback);
  }

  /**
   * Unsubscribe from events
   */
  public off(event: EventType, callback: EventCallback) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }
  }

  /**
   * Emit event to all subscribers
   */
  private emit(event: EventType, data?: any) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[NetworkReconnectionManager] Error in ${event} listener:`, error);
        }
      });
    }
  }

  /**
   * Get current reconnection state
   */
  public getState() {
    return {
      isReconnecting: this.isReconnecting,
      reconnectionAttempt: this.reconnectionAttempt
    };
  }

  /**
   * Cleanup and destroy the manager
   * Removes event listeners and clears timers
   */
  public destroy() {
    console.log('[NetworkReconnectionManager] Destroying instance');

    // Stop reconnection attempts
    this.stopReconnectionAttempts();

    // Remove browser event listeners
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }

    if (this.offlineHandler) {
      window.removeEventListener('offline', this.offlineHandler);
      this.offlineHandler = null;
    }

    // Clear all event listeners
    this.eventListeners.clear();

    // Clear singleton instance
    NetworkReconnectionManager.instance = null;
  }
}

// Export singleton instance getter
export const getNetworkReconnectionManager = () => NetworkReconnectionManager.getInstance();

export default NetworkReconnectionManager;
